import React, { ComponentType, ReactNode } from "react";

import {
  CloseIconButton,
  CopyIconButton,
  EditIconButton,
} from "veneer/IconButton";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";

import { DashboardLayout, DashboardWidgetLayout } from "./dashboardLayout";

declare function require(moduleName: string): unknown;

export interface GridLayoutItem {
  h: number;
  i: string;
  maxH?: number;
  minH?: number;
  minW?: number;
  w: number;
  x: number;
  y: number;
}

export interface GridLayouts {
  lg?: ReadonlyArray<GridLayoutItem>;
  xs?: ReadonlyArray<GridLayoutItem>;
}

export interface GridDroppingItem {
  h: number;
  i: string;
  w: number;
}

export type GridDropCallback = (
  layout: ReadonlyArray<GridLayoutItem>,
  item: GridLayoutItem,
  event: DragEvent,
) => void;

export type GridDropDragOverCallback = (
  event: DragEvent,
) => Pick<GridDroppingItem, "h" | "w"> | false | undefined;

export type GridDragCallback = (
  layout: ReadonlyArray<GridLayoutItem>,
  oldItem: GridLayoutItem,
  newItem: GridLayoutItem,
  placeholder?: GridLayoutItem,
) => void;

interface ResponsiveGridProps {
  breakpoints: Record<string, number>;
  children: ReactNode;
  className?: string;
  cols: Record<string, number>;
  compactType?: "horizontal" | "vertical" | null;
  containerPadding?: [number, number];
  draggableCancel?: string;
  droppingItem?: GridDroppingItem;
  isDraggable?: boolean;
  isDroppable?: boolean;
  isResizable?: boolean;
  layouts: GridLayouts;
  margin?: [number, number];
  onDrag?: GridDragCallback;
  onDragStart?: GridDragCallback;
  onDragStop?: GridDragCallback;
  onDrop?: GridDropCallback;
  onDropDragOver?: GridDropDragOverCallback;
  onLayoutChange?: (
    layout: ReadonlyArray<GridLayoutItem>,
    layouts: GridLayouts,
  ) => void;
  onResizeStart?: GridDragCallback;
  onResizeStop?: GridDragCallback;
  resizeHandles?: ReadonlyArray<"se">;
  rowHeight?: number;
}

interface GridLayoutModule {
  Responsive: ComponentType<ResponsiveGridProps>;
  WidthProvider: (
    component: ComponentType<ResponsiveGridProps>,
  ) => ComponentType<Omit<ResponsiveGridProps, "width">>;
}

const { Responsive, WidthProvider } =
  require("react-grid-layout") as GridLayoutModule;
const ResponsiveDashboardGrid = WidthProvider(Responsive);

export const DASHBOARD_COLUMN_COUNT = 12;
export const DASHBOARD_GRID_GAP = 16;
export const DASHBOARD_ROW_HEIGHT = 45;

const DASHBOARD_GUIDE_EXTRA_ROWS = 4;
const DASHBOARD_GUIDE_MINIMUM_ROWS = 12;

const GridShell = styled("div", {
  shouldForwardProp: (prop) => prop !== "editMinHeight",
})<{ editMinHeight?: number }>(({ editMinHeight }) => ({
  minHeight: editMinHeight,
  position: "relative",
  ".react-grid-layout": {
    minHeight: "inherit",
    position: "relative",
    transition: "height 200ms ease",
    zIndex: 1,
  },
  ".react-grid-item": {
    transition: "transform 200ms ease, width 200ms ease, height 200ms ease",
  },
  ".react-grid-item.cssTransforms": {
    transitionProperty: "transform, width, height",
  },
  ".react-grid-item:hover": {
    zIndex: 2,
  },
  ".react-grid-item.react-draggable-dragging, .react-grid-item.resizing": {
    transition: "none",
    zIndex: 3,
  },
  ".react-grid-placeholder": {
    backgroundColor: Colors.blue200,
    border: `2px dashed ${Colors.blue600}`,
    borderRadius: 8,
    opacity: 0.65,
  },
  ".react-resizable-handle": {
    bottom: 0,
    cursor: "nwse-resize",
    display: "none",
    height: 28,
    opacity: 0,
    position: "absolute",
    right: 0,
    transition: "opacity 120ms ease-in-out",
    width: 28,
    zIndex: 2,
  },
  "&.dashboard-grid-editing .react-resizable-handle": {
    display: "block",
  },
  ".react-resizable-handle:hover, .react-grid-item.resizing .react-resizable-handle":
    {
      opacity: 1,
    },
  ".react-resizable-handle::after": {
    backgroundColor: Colors.gray500,
    border: 0,
    bottom: 4,
    clipPath: "polygon(100% 0, 100% 100%, 0 100%)",
    content: '""',
    height: 12,
    position: "absolute",
    right: 4,
    transform: "none",
    width: 12,
  },
  "@media (max-width: 900px)": {
    minHeight: 0,
  },
}));

const GridGuide = styled("div")({
  display: "grid",
  gap: DASHBOARD_GRID_GAP,
  gridAutoRows: DASHBOARD_ROW_HEIGHT,
  gridTemplateColumns: `repeat(${DASHBOARD_COLUMN_COUNT}, minmax(0, 1fr))`,
  inset: 0,
  pointerEvents: "none",
  position: "absolute",
  zIndex: 0,
  "@media (max-width: 900px)": {
    display: "none",
  },
});

const GridGuideCell = styled("div")({
  backgroundColor: Colors.white40,
  border: `1px solid ${Colors.gray200}`,
  borderRadius: 8,
});

const GridItem = styled("div")({
  display: "flex",
  height: "100%",
  minWidth: 0,
  position: "relative",
  "& > *": {
    flex: 1,
    minWidth: 0,
  },
});

const EditableWidget = styled("div")({
  border: `2px dashed ${Colors.gray500}`,
  borderRadius: 8,
  cursor: "grab",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minWidth: 0,
  position: "relative",
  userSelect: "none",
  "&:active": {
    cursor: "grabbing",
  },
  "&:hover .dashboard-widget-actions": {
    opacity: 1,
    pointerEvents: "auto",
    visibility: "visible",
  },
});

const WidgetActions = styled("div")({
  alignItems: "center",
  backgroundColor: Colors.white,
  border: `1px solid ${Colors.gray300}`,
  borderRadius: 8,
  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.16)",
  cursor: "default",
  display: "flex",
  opacity: 0,
  padding: 2,
  pointerEvents: "none",
  position: "absolute",
  right: 8,
  top: 4,
  transform: "translateY(-50%)",
  transition: "opacity 120ms ease-in-out, visibility 120ms ease-in-out",
  visibility: "hidden",
  zIndex: 4,
});

const WidgetContent = styled("div")({
  display: "flex",
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  pointerEvents: "none",
});

function getGridLayouts<WidgetType extends string>(
  layout: DashboardLayout<WidgetType>,
): GridLayouts {
  let mobileY = 0;
  const lg = layout.widgets.map((widget) => ({
    h: widget.height,
    i: widget.id,
    maxH: 14,
    minH: 1,
    minW: 2,
    w: widget.width,
    x: widget.x,
    y: widget.y,
  }));
  const xs = layout.widgets.map((widget) => {
    const gridItem = {
      h: widget.height,
      i: widget.id,
      maxH: 14,
      minH: 1,
      minW: 1,
      w: 1,
      x: 0,
      y: mobileY,
    };
    mobileY += widget.height;
    return gridItem;
  });

  return { lg, xs };
}

function getGuideRowCount<WidgetType extends string>(
  layout: DashboardLayout<WidgetType>,
) {
  const occupiedRows = layout.widgets.reduce(
    (bottom, widget) => Math.max(bottom, widget.y + widget.height),
    0,
  );
  return Math.max(
    DASHBOARD_GUIDE_MINIMUM_ROWS,
    occupiedRows + DASHBOARD_GUIDE_EXTRA_ROWS,
  );
}

interface DashboardGridProps<WidgetType extends string> {
  dataTestId?: string;
  droppingItem?: GridDroppingItem;
  getWidgetTitle: (type: WidgetType) => string;
  isEditing: boolean;
  isWidgetConfigurable: (widget: DashboardWidgetLayout<WidgetType>) => boolean;
  layout: DashboardLayout<WidgetType>;
  onConfigureWidget: (widget: DashboardWidgetLayout<WidgetType>) => void;
  onDrag?: GridDragCallback;
  onDragStart?: GridDragCallback;
  onDragStop?: GridDragCallback;
  onDrop?: GridDropCallback;
  onDropDragOver?: GridDropDragOverCallback;
  onDuplicateWidget: (widget: DashboardWidgetLayout<WidgetType>) => void;
  onHideWidget: (widget: DashboardWidgetLayout<WidgetType>) => void;
  onLayoutChange?: ResponsiveGridProps["onLayoutChange"];
  onResizeStart?: GridDragCallback;
  onResizeStop?: GridDragCallback;
  renderWidget: (widget: DashboardWidgetLayout<WidgetType>) => ReactNode;
}

export default function DashboardGrid<WidgetType extends string>({
  dataTestId = "dashboard-grid",
  droppingItem,
  getWidgetTitle,
  isEditing,
  isWidgetConfigurable,
  layout,
  onConfigureWidget,
  onDrag,
  onDragStart,
  onDragStop,
  onDrop,
  onDropDragOver,
  onDuplicateWidget,
  onHideWidget,
  onLayoutChange,
  onResizeStart,
  onResizeStop,
  renderWidget,
}: DashboardGridProps<WidgetType>) {
  const guideRowCount = getGuideRowCount(layout);
  const editMinHeight = isEditing
    ? guideRowCount * DASHBOARD_ROW_HEIGHT +
      Math.max(0, guideRowCount - 1) * DASHBOARD_GRID_GAP
    : undefined;

  return (
    <GridShell
      className={isEditing ? "dashboard-grid-editing" : undefined}
      data-testid={dataTestId}
      editMinHeight={editMinHeight}
    >
      {isEditing && (
        <GridGuide aria-hidden="true">
          {Array.from({
            length: guideRowCount * DASHBOARD_COLUMN_COUNT,
          }).map((_, index) => (
            <GridGuideCell key={index} />
          ))}
        </GridGuide>
      )}
      <ResponsiveDashboardGrid
        breakpoints={{ lg: 901, xs: 0 }}
        cols={{ lg: DASHBOARD_COLUMN_COUNT, xs: 1 }}
        compactType="vertical"
        containerPadding={[0, 0]}
        draggableCancel=".dashboard-widget-actions"
        droppingItem={droppingItem}
        isDraggable={isEditing}
        isDroppable={isEditing && Boolean(droppingItem)}
        isResizable={isEditing}
        layouts={getGridLayouts(layout)}
        margin={[DASHBOARD_GRID_GAP, DASHBOARD_GRID_GAP]}
        onDrag={onDrag}
        onDragStart={onDragStart}
        onDragStop={onDragStop}
        onDrop={onDrop}
        onDropDragOver={onDropDragOver}
        onLayoutChange={onLayoutChange}
        onResizeStart={onResizeStart}
        onResizeStop={onResizeStop}
        resizeHandles={["se"]}
        rowHeight={DASHBOARD_ROW_HEIGHT}
      >
        {layout.widgets.map((widget) => (
          <GridItem
            data-testid={`dashboard-widget-${widget.id}`}
            key={widget.id}
          >
            {isEditing ? (
              <EditableWidget>
                <WidgetActions
                  className="dashboard-widget-actions"
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  {isWidgetConfigurable(widget) && (
                    <>
                      <EditIconButton
                        dataTestId={`dashboard-widget-configure-${widget.id}`}
                        iconSize="small"
                        onClick={() => onConfigureWidget(widget)}
                        size="small"
                        title={`Configure ${getWidgetTitle(widget.type)}`}
                      />
                      <CopyIconButton
                        dataTestId={`dashboard-widget-duplicate-${widget.id}`}
                        iconSize="small"
                        onClick={() => onDuplicateWidget(widget)}
                        size="small"
                        title={`Duplicate ${getWidgetTitle(widget.type)}`}
                      />
                    </>
                  )}
                  <CloseIconButton
                    iconSize="small"
                    onClick={() => onHideWidget(widget)}
                    size="small"
                  />
                </WidgetActions>
                <WidgetContent>{renderWidget(widget)}</WidgetContent>
              </EditableWidget>
            ) : (
              renderWidget(widget)
            )}
          </GridItem>
        ))}
      </ResponsiveDashboardGrid>
    </GridShell>
  );
}
